import React, { useState } from 'react';
import { Download, FileText, Calendar, DollarSign, X, TrendingUp, PieChart, Plus } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/formatters';

interface ModelDetails {
  id: string;
  name: string;
  type: string;
  date: string;
  value: number;
  assumptions: string[];
  metrics: {
    irr: number;
    paybackPeriod: number;
    npv: number;
    roic: number;
  };
  sensitivity: {
    revenue: number[];
    costs: number[];
    wacc: number[];
  };
  status: 'draft' | 'review' | 'approved';
}

const recentModels: ModelDetails[] = [
  {
    id: '1',
    name: 'Tech Corp DCF Model',
    type: 'DCF Analysis',
    date: '2024-03-10',
    value: 120000000,
    assumptions: [
      'Revenue growth: 15% YoY',
      'EBITDA margin: 25%',
      'Terminal growth: 3%',
      'WACC: 12%'
    ],
    metrics: {
      irr: 18.5,
      paybackPeriod: 4.2,
      npv: 25000000,
      roic: 15.8
    },
    sensitivity: {
      revenue: [-10, -5, 0, 5, 10],
      costs: [-8, -4, 0, 4, 8],
      wacc: [10, 11, 12, 13, 14]
    },
    status: 'approved'
  },
  {
    id: '2',
    name: 'Manufacturing Co. Comps',
    type: 'Comparable Analysis',
    date: '2024-03-08',
    value: 85000000,
    assumptions: [
      'EV/EBITDA multiple: 8.5x',
      'EV/Revenue multiple: 2.2x',
      'Control premium: 25%'
    ],
    metrics: {
      irr: 16.2,
      paybackPeriod: 3.8,
      npv: 18000000,
      roic: 14.2
    },
    sensitivity: {
      revenue: [-12, -6, 0, 6, 12],
      costs: [-10, -5, 0, 5, 10],
      wacc: [9, 10, 11, 12, 13]
    },
    status: 'review'
  },
  {
    id: '3',
    name: 'Retail Chain LBO',
    type: 'LBO Analysis',
    date: '2024-03-05',
    value: 150000000,
    assumptions: [
      'Debt/EBITDA: 5.0x',
      'Exit multiple: 9.0x',
      'Holding period: 5 years',
      'Interest rate: 7%'
    ],
    metrics: {
      irr: 22.5,
      paybackPeriod: 5.0,
      npv: 35000000,
      roic: 18.5
    },
    sensitivity: {
      revenue: [-15, -7.5, 0, 7.5, 15],
      costs: [-10, -5, 0, 5, 10],
      wacc: [11, 12, 13, 14, 15]
    },
    status: 'completed'
  }
];

export default function ModelList() {
  const [selectedModel, setSelectedModel] = useState<ModelDetails | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'review':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {recentModels.map((model) => (
        <div
          key={model.id}
          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
          onClick={() => setSelectedModel(model)}
        >
          <div>
            <p className="text-sm font-medium text-gray-900">{model.name}</p>
            <p className="text-sm text-gray-500">{model.type}</p>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              {formatDate(model.date)}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(model.status)}`}>
              {model.status.charAt(0).toUpperCase() + model.status.slice(1)}
            </span>
            <Download className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </div>
        </div>
      ))}

      {/* Model Details Popup */}
      {selectedModel && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">{selectedModel.name}</h3>
              <button onClick={() => setSelectedModel(null)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Key Metrics */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-4">Key Metrics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-blue-700">IRR</span>
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-2xl font-bold text-blue-900">{selectedModel.metrics.irr}%</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-green-700">NPV</span>
                          <DollarSign className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold text-green-900">{formatCurrency(selectedModel.metrics.npv)}</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-purple-700">ROIC</span>
                          <PieChart className="w-4 h-4 text-purple-500" />
                        </div>
                        <p className="text-2xl font-bold text-purple-900">{selectedModel.metrics.roic}%</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-orange-700">Payback</span>
                          <Calendar className="w-4 h-4 text-orange-500" />
                        </div>
                        <p className="text-2xl font-bold text-orange-900">{selectedModel.metrics.paybackPeriod}y</p>
                      </div>
                    </div>
                  </div>

                  {/* Assumptions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-4">Key Assumptions</h4>
                    <ul className="space-y-2">
                      {selectedModel.assumptions.map((assumption, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          {assumption}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Sensitivity Analysis */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Sensitivity Analysis</h4>
                  <div className="space-y-6">
                    <div>
                      <h5 className="text-sm font-medium text-gray-600 mb-2">Revenue Impact</h5>
                      <div className="flex items-center space-x-2">
                        {selectedModel.sensitivity.revenue.map((value, index) => (
                          <div
                            key={index}
                            className="flex-1 text-center"
                          >
                            <div className="bg-blue-100 p-2 rounded">
                              <span className={`text-sm font-medium ${value >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                {value > 0 ? '+' : ''}{value}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium text-gray-600 mb-2">Cost Impact</h5>
                      <div className="flex items-center space-x-2">
                        {selectedModel.sensitivity.costs.map((value, index) => (
                          <div
                            key={index}
                            className="flex-1 text-center"
                          >
                            <div className="bg-green-100 p-2 rounded">
                              <span className={`text-sm font-medium ${value >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {value > 0 ? '+' : ''}{value}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium text-gray-600 mb-2">WACC Scenarios</h5>
                      <div className="flex items-center space-x-2">
                        {selectedModel.sensitivity.wacc.map((value, index) => (
                          <div
                            key={index}
                            className="flex-1 text-center"
                          >
                            <div className="bg-purple-100 p-2 rounded">
                              <span className="text-sm font-medium text-purple-700">
                                {value}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
              <button
                onClick={() => setSelectedModel(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}